import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { log } from "@/lib/log";
import { toast } from "sonner";
import { Check, X, Edit3, AlertTriangle, MapPin, Save, Clock, CalendarClock } from "lucide-react";
import { companyTz, fmtDateTime } from "@/lib/tz";
import { useCompanyTz } from "@/context/CompanyTzContext";

const TABS = [
  { id: "pending", label: "Pending", color: "text-warn" },
  { id: "approved", label: "Approved", color: "text-good" },
  { id: "rejected", label: "Rejected", color: "text-bad" },
];

const MS_PER_HOUR = 3_600_000;

// Rendered in the COMPANY's zone, not the viewer's browser zone.
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = new Intl.DateTimeFormat("en-CA", {
    timeZone: companyTz(), year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(d).reduce((a, x) => ({ ...a, [x.type]: x.value }), {});
  return `${p.year}-${p.month}-${p.day}T${p.hour === "24" ? "00" : p.hour}:${p.minute}`;
}
// Sent as a naive wall-clock reading on purpose: the server resolves it
// against the company's zone, which is the only place that knows whether a
// DST transition sits inside the shift.
function localInputToIso(v) {
  return v || null;
}
/** "YYYY-MM-DDTHH:mm" read in the company's zone → epoch ms. */
function localInputToUtcMs(v) {
  if (!v) return null;
  const guess = new Date(`${v}:00Z`).getTime();
  if (Number.isNaN(guess)) return null;
  // Measure the zone's offset at that moment, then correct for it. Done twice
  // so a reading that sits just past a transition lands on the right side.
  let ms = guess;
  for (let i = 0; i < 2; i += 1) {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: companyTz(), year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    }).formatToParts(new Date(ms)).reduce((a, p) => ({ ...a, [p.type]: p.value }), {});
    const asUtc = Date.UTC(+parts.year, +parts.month - 1, +parts.day,
      parts.hour === "24" ? 0 : +parts.hour, +parts.minute, +parts.second);
    ms = guess + (ms - asUtc);
  }
  return ms;
}
/** A reading the zone skipped (spring forward) resolves to standard time. */
function gapNote(v) {
  if (!v) return "";
  const ms = localInputToUtcMs(v);
  if (ms == null) return "";
  const shown = isoToLocalInput(new Date(ms).toISOString());
  return shown === v ? "" : `${v.slice(11)} does not exist on this date — recorded as ${shown.slice(11)} standard time`;
}
function fmtLocal(iso) {
  return fmtDateTime(iso);
}

export default function Approvals() {
  const { tz } = useCompanyTz();   // re-renders once the company zone is known
  const [tab, setTab] = useState("pending");
  const [summary, setSummary] = useState({ pending: 0, approved: 0, rejected: 0, open: 0 });
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // entry being edited (modal)

  const load = useCallback(async () => {
    const [s, list] = await Promise.all([
      api.get("/time-entries/summary"),
      api.get(`/time-entries?approval_status=${tab}`),
    ]);
    setSummary(s.data);
    setRows(list.data);
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, action, note = "") => {
    try {
      await api.post(`/time-entries/${id}/${action}`, { admin_note: note });
      toast.success(action === "approve" ? "Approved" : "Rejected");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const bulkApprove = async () => {
    if (!window.confirm(`Approve all ${rows.length} pending entries?`)) return;
    for (const r of rows) {
      try {
        await api.post(`/time-entries/${r.id}/approve`, { admin_note: "Bulk approved" });
      } catch (err) {
        log.warn(`Bulk approve failed for ${r.id}:`, err?.message || err);
      }
    }
    toast.success("Bulk approval complete");
    load();
  };

  const totalHours = useMemo(() => rows.reduce((a, b) => a + (b.duration_hours || 0), 0), [rows]);
  const totalFlagged = useMemo(() => rows.filter((r) => r.flagged).length, [rows]);

  // Group rows by (employee, ISO week) into "Full Period" blocks so admins
  // approve a whole timesheet week atomically instead of clicking each row.
  // Once every entry in a block is approved, the block becomes one
  // permanent record for payroll processing (materialization already
  // happens server-side on /timesheets/{id}/approve — this UI just
  // visualizes the pre-payroll grouping).
  const grouped = useMemo(() => {
    const buckets = {};
    rows.forEach((r) => {
      const d = new Date(r.clock_in_at);
      // ISO week start = the Sunday on or before the row's date.
      const wk = new Date(d);
      wk.setDate(d.getDate() - d.getDay());
      const wkIso = wk.toISOString().slice(0, 10);
      const key = `${r.employee_id || r.employee_name}::${wkIso}`;
      if (!buckets[key]) {
        buckets[key] = {
          key,
          employee_id: r.employee_id,
          employee_name: r.employee_name,
          week_start: wkIso,
          entries: [],
          work_hours: 0,
          sick_hours: 0,
          flagged_count: 0,
        };
      }
      const bucket = buckets[key];
      bucket.entries.push(r);
      bucket.work_hours += r.duration_hours || 0;
      if (r.note && r.note.toLowerCase().includes("sick")) {
        bucket.sick_hours += r.duration_hours || 0;
      }
      if (r.flagged) bucket.flagged_count += 1;
    });
    return Object.values(buckets).sort((a, b) => b.week_start.localeCompare(a.week_start));
  }, [rows]);

  return (
    <div className="p-10">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="overline mb-2">§ APPROVALS</div>
          <h1 className="font-heading font-black text-5xl tracking-tight">Time Approvals</h1>
          <p className="text-sm text-muted-foreground mt-2">Review, edit, and approve employee time entries before payroll.</p>
        </div>
        {tab === "pending" && rows.length > 0 && (
          <button data-testid="bulk-approve-btn" onClick={bulkApprove} className="hard-btn px-5 py-3 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2">
            <Check className="w-4 h-4" /> Approve All ({rows.length})
          </button>
        )}
      </div>

      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card label="Pending" val={summary.pending} color="text-warn" />
        <Card label="Approved" val={summary.approved} color="text-good" />
        <Card label="Rejected" val={summary.rejected} color="text-bad" />
        <Card label={`${tab.toUpperCase()} Hours`} val={totalHours.toFixed(2) + "h"} />
      </div>

      {/* Full-Period grouping — one collapsed row per (employee × week). */}
      {grouped.length > 0 && (
        <div className="hard-card mb-6" data-testid="approvals-grouped">
          <div className="border-b-2 border-ink px-4 py-3 flex items-center justify-between">
            <div>
              <div className="overline">§ FULL PERIOD BLOCKS</div>
              <div className="font-mono text-xs text-muted-foreground">Grouped by employee + week · once every entry is approved, the block becomes one permanent payroll record.</div>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest">{grouped.length} block{grouped.length === 1 ? "" : "s"}</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full font-mono text-sm">
              <thead className="bg-secondary">
                <tr>
                  <th className="text-left px-3 py-2 uppercase text-[10px] tracking-widest">Employee</th>
                  <th className="text-left px-3 py-2 uppercase text-[10px] tracking-widest">Week Start</th>
                  <th className="text-right px-3 py-2 uppercase text-[10px] tracking-widest">Total Work Hours</th>
                  <th className="text-right px-3 py-2 uppercase text-[10px] tracking-widest">Total Sick Hours Used</th>
                  <th className="text-right px-3 py-2 uppercase text-[10px] tracking-widest">Entries</th>
                  <th className="text-left px-3 py-2 uppercase text-[10px] tracking-widest">Flags</th>
                  <th className="text-right px-3 py-2 uppercase text-[10px] tracking-widest">Action</th>
                </tr>
              </thead>
              <tbody>
                {grouped.map((b, i) => (
                  <tr key={b.key} className={i % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]"} data-testid={`fullperiod-${b.key}`}>
                    <td className="px-3 py-2 border-t border-ink/20 font-semibold">{b.employee_name}</td>
                    <td className="px-3 py-2 border-t border-ink/20">{b.week_start}</td>
                    <td className="px-3 py-2 border-t border-ink/20 text-right font-black">{b.work_hours.toFixed(2)}h</td>
                    <td className="px-3 py-2 border-t border-ink/20 text-right">{b.sick_hours.toFixed(2)}h</td>
                    <td className="px-3 py-2 border-t border-ink/20 text-right">{b.entries.length}</td>
                    <td className="px-3 py-2 border-t border-ink/20">
                      {b.flagged_count > 0
                        ? <span className="px-1.5 py-0.5 bg-bad text-white text-[10px] uppercase tracking-widest">{b.flagged_count} flag{b.flagged_count === 1 ? "" : "s"}</span>
                        : <span className="px-1.5 py-0.5 bg-good text-white text-[10px] uppercase tracking-widest">OK</span>}
                    </td>
                    <td className="px-3 py-2 border-t border-ink/20 text-right">
                      <button
                        data-testid={`bulk-approve-${b.key}`}
                        onClick={async () => {
                          try {
                            const { data } = await api.post("/time-entries/bulk-approve", {
                              employee_id: b.employee_id,
                              week_start: b.week_start,
                            });
                            toast.success(`Approved ${data.approved} entries · ${b.employee_name}`);
                            await load();
                          } catch (e) { toast.error(formatApiError(e)); }
                        }}
                        className="border-2 border-ink px-3 py-1 font-mono text-[10px] uppercase tracking-widest hover:bg-good hover:border-good hover:text-white inline-flex items-center gap-1"
                      >Approve Block →</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex border-2 border-ink mb-6 w-fit" data-testid="approval-tabs">
        {TABS.map((t, i) => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-5 py-2 font-mono text-xs uppercase tracking-widest ${i > 0 ? "border-l-2 border-ink" : ""} ${tab === t.id ? "bg-ink text-white" : ""}`}
          >
            {t.label} ({summary[t.id]})
          </button>
        ))}
      </div>

      <div className="hard-card overflow-x-auto">
        <table className="w-full font-mono text-sm" data-testid="approvals-table">
          <thead className="bg-ink text-white">
            <tr>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Employee</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Worksite</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Clock In ({companyTz()})</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Clock Out</th>
              <th className="text-right px-3 py-3 uppercase text-xs tracking-widest">Duration</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Flags</th>
              <th className="text-right px-3 py-3 uppercase text-xs tracking-widest">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-muted-foreground">No {tab} entries.</td></tr>}
            {rows.map((e, i) => (
              <tr key={e.id} className={`${i % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]"} ${e.flagged ? "bg-bad/5" : ""}`} data-testid={`approval-row-${e.id}`}>
                <td className="px-3 py-3 border-t border-ink/20">
                  <div className="font-semibold">{e.employee_name}</div>
                  {e.edited && <div className="text-[10px] text-warn mt-0.5 inline-flex items-center gap-1"><Edit3 className="w-3 h-3" /> EDITED</div>}
                </td>
                <td className="px-3 py-3 border-t border-ink/20"><span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {e.worksite_name || "—"}</span></td>
                <td className="px-3 py-3 border-t border-ink/20">{fmtLocal(e.clock_in_at)}</td>
                <td className="px-3 py-3 border-t border-ink/20">{fmtLocal(e.clock_out_at)}</td>
                <td className="px-3 py-3 border-t border-ink/20 text-right font-black">{(e.duration_hours || 0).toFixed(2)}h</td>
                <td className="px-3 py-3 border-t border-ink/20">
                  {e.flagged ? (
                    <div className="flex items-start gap-1.5 max-w-xs">
                      <AlertTriangle className="w-4 h-4 text-bad shrink-0 mt-0.5" />
                      <div className="text-[10px] text-bad leading-tight">{e.flag_reason}</div>
                    </div>
                  ) : (
                    <span className="px-1.5 py-0.5 bg-good text-white text-[10px] uppercase tracking-widest">OK</span>
                  )}
                  {e.admin_note && <div className="text-[10px] text-muted-foreground mt-1 italic">"{e.admin_note}"</div>}
                </td>
                <td className="px-3 py-3 border-t border-ink/20 text-right space-x-1 whitespace-nowrap">
                  <button data-testid={`edit-${e.id}`} onClick={() => setEditing(e)} className="border border-ink px-2 py-1 text-xs uppercase tracking-widest inline-flex items-center gap-1 hover:bg-ink hover:text-white">
                    <Edit3 className="w-3 h-3" /> Edit
                  </button>
                  {tab === "pending" && (
                    <>
                      <button data-testid={`approve-${e.id}`} onClick={() => decide(e.id, "approve")} className="bg-good text-white px-2 py-1 text-xs uppercase tracking-widest inline-flex items-center gap-1 hover:opacity-90">
                        <Check className="w-3 h-3" /> Approve
                      </button>
                      <button data-testid={`reject-${e.id}`} onClick={() => decide(e.id, "reject", window.prompt("Reason for rejection?") || "")} className="border border-bad text-bad px-2 py-1 text-xs uppercase tracking-widest inline-flex items-center gap-1 hover:bg-bad hover:text-white">
                        <X className="w-3 h-3" /> Reject
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditModal entry={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}

      <TimeOffApprovals tab={tab} />
    </div>
  );
}

function TimeOffApprovals({ tab }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get(`/time-off?status=${tab}`);
    setRows(data);
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, action) => {
    const note = action === "reject" ? (window.prompt("Reason?") || "") : "";
    try {
      await api.post(`/time-off/${id}/${action}`, { admin_note: note });
      toast.success(action === "approve" ? "Approved (balance updated)" : "Rejected");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-10">
      <div className="overline mb-2 flex items-center gap-2"><CalendarClock className="w-4 h-4" /> § TIME-OFF REQUESTS</div>
      <h2 className="font-heading font-black text-3xl tracking-tight mb-4">Time Off · {tab}</h2>
      <div className="hard-card overflow-x-auto">
        <table className="w-full font-mono text-sm" data-testid="time-off-approvals-table">
          <thead className="bg-ink text-white">
            <tr>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Employee</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Type</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Dates</th>
              <th className="text-right px-3 py-3 uppercase text-xs tracking-widest">Hours</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Note</th>
              {tab === "pending" && <th className="text-right px-3 py-3 uppercase text-xs tracking-widest">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">No {tab} time-off requests.</td></tr>}
            {rows.map((r, i) => (
              <tr key={r.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]"} data-testid={`time-off-approval-${r.id}`}>
                <td className="px-3 py-3 border-t border-ink/20 font-semibold">{r.employee_name}</td>
                <td className="px-3 py-3 border-t border-ink/20 uppercase">{r.type}</td>
                <td className="px-3 py-3 border-t border-ink/20">{r.start_date} → {r.end_date}</td>
                <td className="px-3 py-3 border-t border-ink/20 text-right font-black">{r.hours.toFixed(2)}h</td>
                <td className="px-3 py-3 border-t border-ink/20 italic text-muted-foreground max-w-xs">{r.note || "—"}</td>
                {tab === "pending" && (
                  <td className="px-3 py-3 border-t border-ink/20 text-right space-x-1 whitespace-nowrap">
                    <button data-testid={`approve-time-off-${r.id}`} onClick={() => decide(r.id, "approve")} className="bg-good text-white px-2 py-1 text-xs uppercase tracking-widest inline-flex items-center gap-1 hover:opacity-90">
                      <Check className="w-3 h-3" /> Approve
                    </button>
                    <button data-testid={`reject-time-off-${r.id}`} onClick={() => decide(r.id, "reject")} className="border border-bad text-bad px-2 py-1 text-xs uppercase tracking-widest inline-flex items-center gap-1 hover:bg-bad hover:text-white">
                      <X className="w-3 h-3" /> Reject
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Card({ label, val, color = "" }) {
  return (
    <div className="hard-card p-4">
      <div className="overline mb-1">{label}</div>
      <div className={`font-mono font-black text-3xl ${color}`}>{val}</div>
    </div>
  );
}

function EditModal({ entry, onClose, onSaved }) {
  const [ci, setCi] = useState(isoToLocalInput(entry.clock_in_at));
  const [co, setCo] = useState(isoToLocalInput(entry.clock_out_at));
  const [note, setNote] = useState(entry.admin_note || "");
  const [busy, setBusy] = useState(false);

  const computedHours = useMemo(() => {
    // Resolve both readings in the COMPANY's zone before subtracting, the same
    // way the server does. `new Date("2026-03-07T23:00")` parses in the
    // browser's zone, which showed 4.00h for a shift the server pays 3.0h on.
    if (!ci || !co) return 0;
    const a = localInputToUtcMs(ci); const b = localInputToUtcMs(co);
    if (a == null || b == null) return 0;
    return Math.max(0, (b - a) / MS_PER_HOUR);
  }, [ci, co]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/time-entries/${entry.id}`, {
        clock_in_at: localInputToIso(ci),
        clock_out_at: localInputToIso(co),
        admin_note: note,
      });
      toast.success("Entry updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const saveAndApprove = async () => {
    setBusy(true);
    try {
      await api.patch(`/time-entries/${entry.id}`, {
        clock_in_at: localInputToIso(ci),
        clock_out_at: localInputToIso(co),
        admin_note: note,
      });
      await api.post(`/time-entries/${entry.id}/approve`, { admin_note: note });
      toast.success("Saved & approved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-ink/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white border-2 border-ink w-full max-w-lg shadow-hard-lg">
        <div className="border-b-2 border-ink px-5 py-4">
          <div className="overline">§ EDIT TIME ENTRY</div>
          <h2 className="font-heading font-black text-xl">{entry.employee_name}</h2>
          <div className="text-xs font-mono text-muted-foreground mt-1"><MapPin className="w-3 h-3 inline mr-1" />{entry.worksite_name || "No worksite"}</div>
        </div>
        <div className="p-5 space-y-4">
          <label className="block">
            <div className="overline mb-2 flex items-center gap-1"><Clock className="w-3 h-3" /> Clock In</div>
            <input data-testid="edit-clock-in" type="datetime-local" value={ci} onChange={(e) => setCi(e.target.value)} className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
          </label>
          <label className="block">
            <div className="overline mb-2 flex items-center gap-1"><Clock className="w-3 h-3" /> Clock Out</div>
            <input data-testid="edit-clock-out" type="datetime-local" value={co} onChange={(e) => setCo(e.target.value)} className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" />
            {(gapNote(ci) || gapNote(co)) && (
              <div data-testid="dst-gap-note" className="mt-2 font-mono text-xs text-amber-700">
                {gapNote(ci) || gapNote(co)}
              </div>
            )}
          </label>
          <div className="border-2 border-ink p-3 flex items-center justify-between font-mono text-sm">
            <span className="overline">Recalculated Duration</span>
            <span className="font-black text-lg" data-testid="computed-hours">{computedHours.toFixed(2)}h</span>
          </div>
          <label className="block">
            <div className="overline mb-2">Admin Note</div>
            <textarea data-testid="edit-admin-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="w-full px-3 py-2 border-2 border-ink font-mono text-sm" placeholder="e.g., Adjusted per employee: lunch break excluded" />
          </label>
        </div>
        <div className="border-t-2 border-ink px-5 py-4 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 border-2 border-ink font-mono text-xs uppercase tracking-widest">Cancel</button>
          <button data-testid="edit-save" disabled={busy} onClick={save} className="border-2 border-ink px-4 py-2 font-mono text-xs uppercase tracking-widest inline-flex items-center gap-1 hover:bg-secondary">
            <Save className="w-3.5 h-3.5" /> Save
          </button>
          {entry.approval_status === "pending" && (
            <button data-testid="edit-save-approve" disabled={busy} onClick={saveAndApprove} className="hard-btn px-4 py-2 font-mono text-xs uppercase tracking-widest inline-flex items-center gap-1">
              <Check className="w-3.5 h-3.5" /> Save & Approve
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
